import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { WheelConditionLevel } from '../constants/openMarketProduct/openMarketProduct';
import { MachineType } from '../interfaces/Brand.interfaces';
import { IMachineListMachine } from '../interfaces/Dictionary.interfaces';

export const imageTypes = [
  'LEFT',
  'FRONT',
  'BACK',
  'INNER',
  'WORKINGMACHINE1',
  'FRONTTIER',
  'BACKTIER',
  // TODO: 추후 판매 트랙터 -> 판매 농기계로 리팩터링시 분리하거나 정리 필요
  'LEFTTRACK',
  'RIGHTTRACK',
  'ETC1',
  'ETC2',
  'ETC3',
] as const;

export type tractorImageType = (typeof imageTypes)[number];
export type tractorImageObject = Record<tractorImageType, string>;
export type sellTractorMachineInfo = Pick<IMachineListMachine, 'id' | 'approvalStatus' | 'engineOutputInPs'>;

interface userSellTractorRequiredValue {
  brand?: string;
  brandId?: string;
  model?: string;
  sellTractorMachineInfo?: sellTractorMachineInfo;
  engineOutputInPs?: number;
  manufacturedYear?: number;
  operationTime?: number;
  machineType?: MachineType;
  rowConfig?: string;
}

export interface userSellTractorSurveyStateType extends userSellTractorRequiredValue {
  sellTractorIdForEdit?: string;
  userOwnTractorId?: string;
  userOwnTractorMarketPrice?: number;
  option?: string;
  optionDetails?: string[];
  workingMachine?: string;
  workingMachineDetails?: string[];
  replacedParts?: string[];
  needRepairParts?: string[];
  frontTireCondition?: WheelConditionLevel;
  rearTireCondition?: WheelConditionLevel;
  leftTrackCondition?: WheelConditionLevel;
  rightTrackCondition?: WheelConditionLevel;
  information?: string;
  tractorImageUrls: tractorImageObject;
  location?: string;
  hopeSellingPrice?: number;
  directImportYn?: boolean;
  priceNegotiationYn?: boolean;
}

const initialState: userSellTractorSurveyStateType = {
  machineType: undefined,
  sellTractorIdForEdit: undefined,
  userOwnTractorId: undefined,
  userOwnTractorMarketPrice: undefined,
  brand: undefined,
  brandId: undefined,
  model: undefined,
  sellTractorMachineInfo: undefined,
  engineOutputInPs: undefined,
  rowConfig: undefined,
  manufacturedYear: undefined,
  operationTime: undefined,
  option: undefined,
  optionDetails: undefined,
  workingMachine: undefined,
  workingMachineDetails: undefined,
  replacedParts: undefined,
  needRepairParts: undefined,
  frontTireCondition: undefined,
  rearTireCondition: undefined,
  leftTrackCondition: undefined,
  rightTrackCondition: undefined,
  information: undefined,
  tractorImageUrls: {
    LEFT: '',
    FRONT: '',
    BACK: '',
    INNER: '',
    WORKINGMACHINE1: '',
    FRONTTIER: '',
    BACKTIER: '',
    LEFTTRACK: '',
    RIGHTTRACK: '',
    ETC1: '',
    ETC2: '',
    ETC3: '',
  },
  location: undefined,
  hopeSellingPrice: undefined,
  directImportYn: undefined,
  priceNegotiationYn: undefined,
};

export const userSellTractorSurveySlice = createSlice({
  name: 'userSellTractorSurvey',
  initialState,
  reducers: {
    resetState: (state) => {
      state.machineType = undefined;
      state.sellTractorIdForEdit = undefined;
      state.userOwnTractorId = undefined;
      state.userOwnTractorMarketPrice = undefined;
      state.brand = undefined;
      state.brandId = undefined;
      state.model = undefined;
      state.sellTractorMachineInfo = undefined;
      state.engineOutputInPs = undefined;
      state.rowConfig = undefined;
      state.manufacturedYear = undefined;
      state.operationTime = undefined;
      state.option = undefined;
      state.optionDetails = undefined;
      state.workingMachine = undefined;
      state.workingMachineDetails = undefined;
      state.replacedParts = undefined;
      state.needRepairParts = undefined;
      state.frontTireCondition = undefined;
      state.rearTireCondition = undefined;
      state.leftTrackCondition = undefined;
      state.rightTrackCondition = undefined;
      state.information = undefined;
      state.tractorImageUrls = {
        LEFT: '',
        FRONT: '',
        BACK: '',
        INNER: '',
        WORKINGMACHINE1: '',
        FRONTTIER: '',
        BACKTIER: '',
        LEFTTRACK: '',
        RIGHTTRACK: '',
        ETC1: '',
        ETC2: '',
        ETC3: '',
      };
      state.location = undefined;
      state.hopeSellingPrice = undefined;
      state.directImportYn = undefined;
      state.priceNegotiationYn = undefined;
    },

    changeAllStates: (state, action: PayloadAction<userSellTractorSurveyStateType>) => {
      state.machineType = action.payload.machineType;
      state.sellTractorIdForEdit = action.payload.sellTractorIdForEdit;
      state.userOwnTractorId = action.payload.userOwnTractorId;
      state.brand = action.payload.brand;
      state.brandId = action.payload.brandId;
      state.model = action.payload.model;
      state.sellTractorMachineInfo = action.payload.sellTractorMachineInfo;
      state.engineOutputInPs = action.payload.engineOutputInPs;
      state.rowConfig = action.payload.rowConfig;
      state.manufacturedYear = action.payload.manufacturedYear;
      state.operationTime = action.payload.operationTime;
      state.option = action.payload.option;
      state.optionDetails = action.payload.optionDetails;
      state.workingMachine = action.payload.workingMachine;
      state.workingMachineDetails = action.payload.workingMachineDetails;
      state.replacedParts = action.payload.replacedParts;
      state.needRepairParts = action.payload.needRepairParts;
      state.frontTireCondition = action.payload.frontTireCondition;
      state.rearTireCondition = action.payload.rearTireCondition;
      state.leftTrackCondition = action.payload.leftTrackCondition;
      state.rightTrackCondition = action.payload.rightTrackCondition;
      state.information = action.payload.information;
      state.tractorImageUrls = action.payload.tractorImageUrls;
      state.location = action.payload.location;
      state.hopeSellingPrice = action.payload.hopeSellingPrice;
      state.directImportYn = action.payload.directImportYn;
      state.priceNegotiationYn = action.payload.priceNegotiationYn;
    },

    changeSellTractorSurveyForEdit: (
      state,
      action: PayloadAction<Omit<userSellTractorSurveyStateType, 'userOwnTractorId' | 'userOwnTractorMarketPrice'>>,
    ) => {
      state.machineType = action.payload.machineType;
      state.sellTractorIdForEdit = action.payload.sellTractorIdForEdit;
      state.brand = action.payload.brand;
      state.brandId = action.payload.brandId;
      state.model = action.payload.model;
      state.sellTractorMachineInfo = action.payload.sellTractorMachineInfo;
      state.engineOutputInPs = action.payload.engineOutputInPs;
      state.rowConfig = action.payload.rowConfig;
      state.manufacturedYear = action.payload.manufacturedYear;
      state.operationTime = action.payload.operationTime;
      state.option = action.payload.option;
      state.optionDetails = action.payload.optionDetails;
      state.workingMachine = action.payload.workingMachine;
      state.workingMachineDetails = action.payload.workingMachineDetails;
      state.replacedParts = action.payload.replacedParts;
      state.needRepairParts = action.payload.needRepairParts;
      state.frontTireCondition = action.payload.frontTireCondition;
      state.rearTireCondition = action.payload.rearTireCondition;
      state.leftTrackCondition = action.payload.leftTrackCondition;
      state.rightTrackCondition = action.payload.rightTrackCondition;
      state.information = action.payload.information;
      state.tractorImageUrls = action.payload.tractorImageUrls;
      state.location = action.payload.location;
      state.hopeSellingPrice = action.payload.hopeSellingPrice;
      state.directImportYn = action.payload.directImportYn;
      state.priceNegotiationYn = action.payload.priceNegotiationYn;
    },

    changeMachineType: (state, action: PayloadAction<MachineType>) => {
      state.machineType = action.payload;
    },

    changeUserOwnTractorId: (state, action: PayloadAction<string>) => {
      state.userOwnTractorId = action.payload;
    },

    changeSellTractorRequiredInputs: (state, action: PayloadAction<userSellTractorRequiredValue>) => {
      state.machineType = action.payload.machineType;
      state.brand = action.payload.brand;
      state.brandId = action.payload.brandId;
      state.model = action.payload.model;
      state.sellTractorMachineInfo = action.payload.sellTractorMachineInfo;
      state.engineOutputInPs = action.payload.engineOutputInPs;
      state.rowConfig = action.payload.rowConfig;
      state.manufacturedYear = action.payload.manufacturedYear;
      state.operationTime = action.payload.operationTime;
    },

    changeUserOwnTractorMarketPrice: (state, action: PayloadAction<number>) => {
      state.userOwnTractorMarketPrice = action.payload;
    },

    changeOption: (state, action: PayloadAction<string>) => {
      state.option = action.payload;
    },

    changeOptionDetails: (state, action: PayloadAction<string[]>) => {
      state.optionDetails = action.payload;
    },

    changeWorkingMachine: (state, action: PayloadAction<string>) => {
      state.workingMachine = action.payload;
    },

    changeWorkingMachineDetails: (state, action: PayloadAction<string[]>) => {
      state.workingMachineDetails = action.payload;
    },

    changeReplacedParts: (state, action: PayloadAction<string[]>) => {
      state.replacedParts = action.payload;
    },

    changeNeedRepairParts: (state, action: PayloadAction<string[]>) => {
      state.needRepairParts = action.payload;
    },

    changeFrontTireCondition: (state, action: PayloadAction<WheelConditionLevel>) => {
      state.frontTireCondition = action.payload;
    },

    changeRearTireCondition: (state, action: PayloadAction<WheelConditionLevel>) => {
      state.rearTireCondition = action.payload;
    },

    changeLeftTrackCondition: (state, action: PayloadAction<WheelConditionLevel>) => {
      state.leftTrackCondition = action.payload;
    },

    changeRightTrackCondition: (state, action: PayloadAction<WheelConditionLevel>) => {
      state.rightTrackCondition = action.payload;
    },

    changeInformation: (state, action: PayloadAction<string>) => {
      state.information = action.payload;
    },

    changeTractorImageUrls: (state, action: PayloadAction<{ property: tractorImageType; imageUrl: string }>) => {
      state.tractorImageUrls[action.payload.property] = action.payload.imageUrl;
    },

    changeLocation: (state, action: PayloadAction<string>) => {
      state.location = action.payload;
    },

    changeHopeSellingPrice: (state, action: PayloadAction<number>) => {
      state.hopeSellingPrice = action.payload;
    },

    changeDirectImportYn: (state, action: PayloadAction<boolean>) => {
      state.directImportYn = action.payload;
    },

    changePriceNegotiationYn: (state, action: PayloadAction<boolean>) => {
      state.priceNegotiationYn = action.payload;
    },
  },
});
